/* eslint-disable @typescript-eslint/no-unused-vars */

'use client';

import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { CommonForm } from '@/components/CommonForm';
import { CommonHidden } from '@/components/CommonForm/CommonHidden';

import { useCustomSearchParams, useFormApi } from '@lamesarv-sdk/hooks';
// import { useDynamicsForms } from '@/hooks/dynamicsForms';
import { useEnv } from '@/hooks/useEnv';

import { Modal } from '@/components/Modal/Modal';

import { IEventRegistrationFormProps } from './types';

import { GeneralForm } from './Types/General';
import { InfluencerForm } from './Types/Influencer';
import { IFormInput } from '@lamesarv-sdk/types';
import { generalSchema, influencerSchema } from './schemas';

interface IInventoryForm {
  leadType: string;
  leadSource: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  location: string;
  contact: string;
  product: string;
  comments: string;
  terms: boolean;
  subscribe: boolean;
}

// --

const FormReset = ({ wasSuccessful }: { wasSuccessful: boolean }) => {
  const { reset } = useFormContext();

  useEffect(() => {
    if (!wasSuccessful) return;

    reset();
    reset({
      phone: '',
    });
  }, [wasSuccessful]);

  return null;
};

// --

const mapTypes = {
  general: GeneralForm,
  influencer: InfluencerForm,
};

const mapSchemas = {
  general: generalSchema,
  influencer: influencerSchema,
};

const mapTags = {
  general: 671,
  influencer: 670,
};

const FormBase = (props: IEventRegistrationFormProps) => {
  const [loadingLegacy, submittedLegacy, errorLegacy, sendFormLegacy] = useFormApi();
  // const { loading, submitted, error } = useDynamicsForms();

  const { env } = useEnv();

  const wasSuccessful = !loadingLegacy && !errorLegacy && submittedLegacy;
  const wasError = !loadingLegacy && errorLegacy && submittedLegacy;

  const onSubmit = async (data: IInventoryForm) => {
    const tagId = mapTags[props.form_type] ?? mapTags.general;
    /*
     * ToDo: Implement Dynamics Form
     *
    const formMappings = [
      {
        DataverseFieldName: 'lmrv_leadtype',
        DataverseFieldValue: data.leadType || props.custom_form_leadtype || String(DynamicsLeadType.buyer),
      },
      {
        DataverseFieldName: 'leadsourcecode',
        DataverseFieldValue: data.leadSource || props.custom_form_leadsource || 'lamesarv',
      },
      {
        DataverseFieldName: 'firstname',
        DataverseFieldValue: data.firstName || '',
      },
      {
        DataverseFieldName: 'lastname',
        DataverseFieldValue: data.lastName || '',
      },
      {
        DataverseFieldName: 'emailaddress1',
        DataverseFieldValue: data.email || '',
      },
      {
        DataverseFieldName: 'mobilephone',
        DataverseFieldValue: data.phone ? parsePhone(data.phone) : '',
      },
      // ToDo: map missing fields:
      // - attendance
      // - interests
      // - company_name
      // - website_url
      // - followers_number
      {
        DataverseFieldName: 'lmrv_comments',
        DataverseFieldValue: data.comments || '',
      },
      {
        DataverseFieldName: 'lmrv_accepttermsandconditions',
        DataverseFieldValue: data.terms ? '1' : '0',
      },
      {
        DataverseFieldName: 'lmrv_subscribe',
        DataverseFieldValue: data.subscribe ? '1' : '0',
      },
    ];

    await sendForm({
      formId: `#${props.custom_id || 'eventRegistrationForm'}`,
      mappings: formMappings,
      dynamicsFormId: env.DYNAMICS_FORM_ID_EVENT || props.custom_dynamics_id,
    });
    */

    if (env.ENABLE_FORMS_LEGACY) {
      await sendFormLegacy({
        ...data,
        source_url: window.location.href,
        extra_tag_ids: [tagId],
      } as unknown as IFormInput);
    }
  };

  useEffect(() => {
    if (wasSuccessful && props.onSuccess) {
      props.onSuccess();
    }
  }, [wasSuccessful]);

  const FormTyped = mapTypes[props.form_type] ?? mapTypes.general;
  const formSchema = mapSchemas[props.form_type] ?? mapSchemas.general;

  return (
    <CommonForm
      id={props.custom_id || 'eventRegistrationForm'}
      schema={formSchema}
      onSubmit={onSubmit}
      isDisabled={loadingLegacy || wasSuccessful}
      tagGroup={props.tag_group || 'form'}
      tagItem={props.tag ? `form-event-registration-${props.tag}` : 'form-event-registration'}
      className="font-helvetica"
    >
      <CommonHidden name="formId" value={props.custom_active_campaign_id || '00'} />
      <CommonHidden name="formType" value={props.custom_form_type || 'super_b_rv_show'} />
      <CommonHidden name="leadType" value={props.custom_form_leadtype || 'buyer'} />
      <CommonHidden name="leadSource" value={props.custom_form_leadsource || 'lamesarv'} />
      <FormTyped {...props} wasError={wasError} wasSuccessful={wasSuccessful} />
      <FormReset wasSuccessful={wasSuccessful} />
    </CommonForm>
  );
};

export const GenericEventRegistrationForm = (props: IEventRegistrationFormProps) => {
  const { searchParams } = useCustomSearchParams();
  const modalOpenStatus =
    (props.modal && !props.modal_url_parameter) ||
    (props.modal && props.modal_url_parameter && !!searchParams.get(props.modal_url_parameter));
  const [modalOpen, setModalOpen] = useState(modalOpenStatus);

  if (props.modal) {
    return (
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} useMaxWidth={false} className="">
        <FormBase
          {...props}
          onSuccess={() =>
            setTimeout(() => {
              setModalOpen(false);
            }, 2000)
          }
        />
      </Modal>
    );
  }

  return <FormBase {...props} />;
};
